/**
 * Vendor Intelligence — provider disclosure vocabulary.
 *
 * Single source of truth for translating a stored provider snapshot
 * (provider id + jurisdiction + lookup status) into ONE truthful,
 * customer-facing state. The six states are never interchangeable:
 *
 *   Verified             connected provider returned a confident match
 *   No Records Identified connected provider searched and found nothing
 *   Not Connected        provider is a development stub, unconfigured,
 *                        or the lookup never executed
 *   Unsupported          provider does not cover this jurisdiction /
 *                        entity / record type
 *   Provider Error       provider was connected but the request failed
 *   Not Applicable       the source does not apply to this transaction
 *
 * RULE: "No Records Identified" may NEVER be used for a stub,
 * disconnected, or unexecuted search.
 */

export type ProviderDisclosure =
  | "Verified"
  | "No Records Identified"
  | "Not Connected"
  | "Unsupported"
  | "Provider Error"
  | "Not Applicable";

/** Provider identifiers that are development fixtures, never real data. */
const STUB_PROVIDERS = new Set(["stub", "dev_stub", "fixture", "demo"]);

/** Jurisdiction codes emitted by stub adapters. */
const STUB_JURISDICTIONS = new Set(["us-stub", "stub"]);

/**
 * True when the snapshot was produced by a development stub adapter.
 * Stub output must never be presented as a real-world search result.
 */
export function isStubProvider(
  provider?: string | null,
  jurisdiction?: string | null,
): boolean {
  const p = (provider ?? "").trim().toLowerCase();
  const j = (jurisdiction ?? "").trim().toLowerCase();
  return STUB_PROVIDERS.has(p) || STUB_JURISDICTIONS.has(j);
}

/** Lookup statuses that mean the provider is not wired up. */
const NOT_CONNECTED_STATUSES = new Set(["misconfigured", "not_configured", "disabled"]);

/** Lookup statuses that mean the request was attempted and failed. */
const ERROR_STATUSES = new Set([
  "provider_error",
  "timeout",
  "quota_exceeded",
  "unavailable",
  "error",
  "invalid_input",
]);

/** Lookup statuses that mean a real search completed with no match. */
const SEARCHED_STATUSES = new Set([
  "not_found",
  "no_results",
  "no_verified_match",
  "multiple_candidates",
]);

/** Lookup statuses that mean the source does not cover this jurisdiction. */
const UNSUPPORTED_STATUSES = new Set([
  "no_coverage",
  "unsupported",
  "license_not_required",
]);

export interface DisclosureInput {
  provider?: string | null;
  jurisdiction?: string | null;
  status?: string | null;
  /** True when a matched profile object is actually present. */
  hasProfile?: boolean;
  /** True when the source is structurally inapplicable to this claim. */
  notApplicable?: boolean;
}

/**
 * Resolves the one truthful customer-facing state for a provider snapshot.
 * A missing snapshot (no status) is "Not Connected" — the lookup never ran.
 */
export function resolveDisclosure(input: DisclosureInput): ProviderDisclosure {
  const status = (input.status ?? "").trim().toLowerCase();

  // Stub output outranks every other signal: no real search occurred.
  if (isStubProvider(input.provider, input.jurisdiction)) return "Not Connected";
  if (!status) return "Not Connected";
  if (NOT_CONNECTED_STATUSES.has(status)) return "Not Connected";
  if (status === "matched" && input.hasProfile) return "Verified";
  if (UNSUPPORTED_STATUSES.has(status)) {
    return input.notApplicable ? "Not Applicable" : "Unsupported";
  }
  if (ERROR_STATUSES.has(status)) return "Provider Error";
  if (SEARCHED_STATUSES.has(status)) return "No Records Identified";
  return "Not Connected";
}

/** Public-safe, provider-agnostic sentence for a disclosure state. */
export function disclosureSentence(
  disclosure: ProviderDisclosure,
  sourceLabel: string,
): string {
  switch (disclosure) {
    // Every sentence is phrased so it reads correctly whether `sourceLabel`
    // is singular ("Secretary of State record") or plural ("State licensing
    // records"). Wording only — the disclosure state itself is unchanged.
    case "Verified":
      return `A confidently matching record was returned from ${sourceLabel}.`;
    case "No Records Identified":
      return `${sourceLabel}: searched successfully; no sufficiently matching record was found. This is a search result, not an adverse finding.`;
    case "Not Connected":
      return `${sourceLabel}: this source is outside ClaimGuard's current data coverage, so no live check was performed. This is a coverage boundary, never an adverse finding.`;
    case "Unsupported":
      return `${sourceLabel}: coverage is not available for this jurisdiction or record type. This is a coverage boundary, never an adverse finding.`;
    case "Provider Error":
      return `${sourceLabel}: the source was reachable, but the verification request did not complete.`;
    case "Not Applicable":
      return `${sourceLabel}: this source does not apply to this vendor or transaction.`;
  }
}
